import React from "react";
import { Box, Typography } from "@mui/material";

import {
    summarizePointsValidity,
    getExpiryColor,
    formatValidityDate,
    parseValidityDate,
    resolveLoyaltyPointsExpiresAt,
} from "../../utils/validityDisplay";
import { isPurchasesMode } from "../../utils/loyaltyQualification";

function ValidityBlock({ title, date, hint, lots = [], showLotsBreakdown = false, emptyLabel }) {
    const color = date ? getExpiryColor(date) : "text.secondary";

    return (
        <Box px={2} py={1.5} bgcolor="#fafbfd" borderRadius={2} border="1px solid #e8ecf1" height="100%">
            <Typography
                fontSize={11}
                color="text.secondary"
                fontWeight={600}
                mb={0.5}
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
                {title}
            </Typography>
            {date ? (
                <Typography fontSize={14} fontWeight={700} color={color}>
                    {formatValidityDate(date)}
                </Typography>
            ) : (
                <Typography fontSize={14} fontWeight={600} color="text.secondary">
                    {emptyLabel || "–"}
                </Typography>
            )}
            {hint && (
                <Typography fontSize={11} color="text.secondary" mt={0.5} lineHeight={1.4}>
                    {hint}
                </Typography>
            )}
            {showLotsBreakdown && lots.length > 1 && (
                <Box mt={1} display="flex" flexDirection="column" gap={0.25}>
                    {lots.slice(0, 5).map((lot) => (
                        <Typography key={lot.dateKey} fontSize={11} color="text.secondary">
                            · {lot.points} pt{lot.points > 1 ? "s" : ""} → {formatValidityDate(lot.date)}
                        </Typography>
                    ))}
                    {lots.length > 5 && (
                        <Typography fontSize={10} color="text.secondary" fontStyle="italic">
                            +{lots.length - 5} autre(s) échéance(s)
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}

/**
 * Dates de validité — uniquement celles du mode de qualification actif.
 * En PURCHASES, les points (wallet) ne sont pas dans ce bloc qualification.
 */
const CustomerValidityDatesPanel = ({
    customer,
    movements = [],
    loyalty = null,
    movementsLoadError = null,
}) => {
    if (!customer) return null;

    const purchases = isPurchasesMode(loyalty?.qualificationMode);
    const statusExpiry = loyalty?.statusExpiresAt
        ?? loyalty?.status_expires_at
        ?? customer.loyalty_status_expires_at;
    const purchasesExpiry = loyalty?.purchasesExpiresAt ?? loyalty?.purchases_expires_at;

    if (purchases) {
        return (
            <Box mb={3} display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
                <ValidityBlock
                    title="Achats valables jusqu'au"
                    date={purchasesExpiry}
                    hint={purchasesExpiry ? null : "Aucune date d’expiration des achats"}
                />
                <ValidityBlock
                    title="Statut de fidélité valable jusqu'au"
                    date={statusExpiry}
                    hint={statusExpiry ? null : "Aucune date de fin de statut enregistrée"}
                />
            </Box>
        );
    }

    const loyaltyExpiry = resolveLoyaltyPointsExpiresAt(loyalty);
    const customerFallback = customer.points_expires_at;

    let pointsSummary;
    if (movementsLoadError) {
        const fallbackDate = loyaltyExpiry ?? customerFallback;
        pointsSummary = {
            displayDate: parseValidityDate(fallbackDate),
            lots: [],
            multiple: false,
            hint: "Impossible de charger l'historique pour le moment",
            source: "error",
        };
    } else if (loyaltyExpiry !== undefined) {
        pointsSummary = summarizePointsValidity(movements, null, loyaltyExpiry);
    } else {
        pointsSummary = summarizePointsValidity(movements, customerFallback);
    }

    const pointsEmptyLabel = pointsSummary.source === "loyalty_none" || pointsSummary.source === "none"
        ? "Aucun point en cours de validité"
        : "–";

    return (
        <Box mb={3} display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
            <ValidityBlock
                title="Points valables jusqu'au"
                date={pointsSummary.displayDate}
                emptyLabel={pointsEmptyLabel}
                hint={pointsSummary.hint}
                lots={pointsSummary.lots}
                showLotsBreakdown={pointsSummary.multiple}
            />
            <ValidityBlock
                title="Statut de fidélité valable jusqu'au"
                date={statusExpiry}
                hint={statusExpiry ? null : "Aucune date de fin de statut enregistrée"}
            />
        </Box>
    );
};

export default CustomerValidityDatesPanel;
